import { Component, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { LoginService } from '../../services/login.service';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: false,
})
export class HeaderComponent implements OnInit {
  languages!: string[];
  selectedLang!: string;
  defaultImage = false;

  username: string | null = '';
  email: string | null = '';
  isProfileMenuOpen = false;

  // Constants to avoid magic strings and reduce duplication
  private readonly LS_LANGUAGE = 'language';
  private readonly LS_TOKEN = 'token';
  private readonly DEFAULT_LANG = 'en';
  private readonly RELEASE_NOTES_URL = 'assets/documents/Release Notes Nash Pro - Okt-2026.pdf';

  constructor(
    private translate: TranslateService,
    private router: Router,
    private login: LoginService,
    private snackBar: MatSnackBar,
  ) {
    // Initialize available languages and default
    this.translate.addLangs([this.DEFAULT_LANG]);
    this.translate.setDefaultLang(this.DEFAULT_LANG);
  }

  ngOnInit(): void {
    this.initLanguage();
    this.initUserInfo();
  }

  openReleaseNotes(): void {
    window.open(this.RELEASE_NOTES_URL, '_blank');
  }

  /** Language switch handler (keeps behavior identical) */
  switchLanguage(lang: string): void {
    this.setLanguage(lang);
  }

  toggleProfile(): void {
    this.isProfileMenuOpen = !this.isProfileMenuOpen;
  }

  /** Logout flow (token-based), same functionality with robust error handling */
  async logout(): Promise<void> {
    const token = localStorage.getItem(this.LS_TOKEN);
    if (!token) {
      // Nothing to do; optionally show a message
      this.showSnack('You are already logged out', 2500);
      return;
    }

    try {
      await this.login.logout(token);
      this.showSnack('You have been successfully logged out', 3000);
      // Optional: route to login if desired
      // await this.router.navigate(['/login']);
    } catch (err) {
      // Preserve behavior by showing feedback on failure
      this.showSnack('Logout failed. Please try again.', 3000, ['snackbar-error']);
    }
  }

  // -----------------------
  // Private helpers (refactor to remove duplication while preserving behavior)
  // -----------------------

  /** Initialize language from localStorage; fall back to default. */
  private initLanguage(): void {
    this.languages = [...this.translate.getLangs()];
    const stored = localStorage.getItem(this.LS_LANGUAGE) || this.DEFAULT_LANG;
    this.setLanguage(stored);
  }

  /** Apply language to TranslateService and persist to localStorage. */
  private setLanguage(lang: string): void {
    this.selectedLang = lang;
    localStorage.setItem(this.LS_LANGUAGE, lang);
    this.translate.use(lang);
  }

  /** Initialize user info if token exists (keeps same behavior). */
  private initUserInfo(): void {
    const token = this.login.getToken();
    if (token) {
      const user = this.login.getUser();
      const first = (user?.firstname ?? '').trim();
      const last = (user?.lastname ?? '').trim();
      const email = (user?.email ?? '').trim();
      const uname = (user?.username ?? '').trim();
      const fullName = `${first} ${last}`.trim();
      this.username = fullName || uname;
      this.email = email;
    } else {
      this.username = '';
      this.email = '';
    }
  }

  /** Show snackbar message with customizable duration and styles. */
  private showSnack(message: string, duration = 3000, panelClass: string[] = []): void {
    this.snackBar.open(message, '', { duration, panelClass });
  }
}
